import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Hero } from '../components/Hero';
import { Checklist } from '../components/Checklist';
import { Button } from '../components/ui/Button';
import { Section } from '../components/ui/Section';
import { Container } from '../components/ui/Container';
import { SEO } from '../components/SEO';
import { PERSONAL_STEPS, ORG_STEPS } from '../utils/constants';
import { Shield, Lock, Award, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
export function Home() {
  const [activeTab, setActiveTab] = useState<'personal' | 'org'>('personal');
  const {
    t
  } = useLanguage();
  return <Layout data-id="element-3649">
      <SEO title="CyberSecurityProtocol | Practical Cybersecurity Protocols for Individuals & Organizations" description="Practical, standards-aligned cybersecurity protocols, risk assessments, and actionable guidance to help individuals and organizations reduce cyber risk and improve digital security readiness." keywords="cybersecurity protocol, security risk assessment, personal security, organizational security, cyber risk, security guidance, standards-aligned security" url="https://cybersecurityprotocol.org" data-id="element-3650" />

      <Hero data-id="element-3651" />

      {/* Audience Switcher & Checklist Section */}
      <Section spacing="xl" className="bg-slate-950" data-id="element-3652">
        <Container size="lg" data-id="element-3653">
          <div className="max-w-4xl mx-auto" data-id="element-3654">
            {/* Mobile-Friendly Tab Switcher */}
            <div className="flex justify-center mb-8 sm:mb-12" data-id="element-3655">
              <div className="bg-slate-900 p-1 rounded-lg inline-flex border border-slate-800 w-full sm:w-auto" data-id="element-3656">
                <button onClick={() => setActiveTab('personal')} className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${activeTab === 'personal' ? 'bg-cyan-500 text-slate-950 shadow-lg' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-3657">
                  {t('home.tab.personal')}
                </button>
                <button onClick={() => setActiveTab('org')} className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${activeTab === 'org' ? 'bg-cyan-500 text-slate-950 shadow-lg' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-3658">
                  {t('home.tab.organization')}
                </button>
              </div>
            </div>

            {/* Responsive Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start" data-id="element-3659">
              <div data-id="element-3660">
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-4" data-id="element-3661">
                  {activeTab === 'personal' ? t('home.personal.title') : t('home.org.title')}
                </h2>
                <p className="text-sm sm:text-base text-slate-400 mb-6" data-id="element-3662">
                  {activeTab === 'personal' ? t('home.personal.desc') : t('home.org.desc')}
                </p>

                {activeTab === 'org' && <p className="text-xs sm:text-sm text-slate-500 mb-6 sm:mb-8 italic" data-id="element-3663">
                    {t('home.org.note')}
                  </p>}

                <div className="space-y-4 sm:space-y-6" data-id="element-3664">
                  <div className="flex items-start" data-id="element-3665">
                    <div className="h-10 w-10 rounded-lg bg-cyan-500/10 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0" data-id="element-3666">
                      <Shield className="h-5 w-5 text-cyan-500" data-id="element-3667" />
                    </div>
                    <div data-id="element-3668">
                      <h3 className="font-bold text-slate-100 text-sm sm:text-base mb-1" data-id="element-3669">
                        {activeTab === 'personal' ? t('home.principle1.title') : t('home.principle1.titleOrg')}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-400" data-id="element-3670">
                        {activeTab === 'personal' ? t('home.principle1.desc') : t('home.principle1.descOrg')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-3671">
                    <div className="h-10 w-10 rounded-lg bg-cyan-500/10 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0" data-id="element-3672">
                      <Lock className="h-5 w-5 text-cyan-500" data-id="element-3673" />
                    </div>
                    <div data-id="element-3674">
                      <h3 className="font-bold text-slate-100 text-sm sm:text-base mb-1" data-id="element-3675">
                        {activeTab === 'personal' ? t('home.principle2.title') : t('home.principle2.titleOrg')}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-400" data-id="element-3676">
                        {activeTab === 'personal' ? t('home.principle2.desc') : t('home.principle2.descOrg')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Checklist id={`home-${activeTab}`} title={t('home.checklist.title')} items={activeTab === 'personal' ? PERSONAL_STEPS : ORG_STEPS} data-id="element-3677" />
            </div>
          </div>
        </Container>
      </Section>

      {/* Enhanced Trust Anchors Section */}
      <Section spacing="lg" className="border-t border-slate-900 bg-slate-950/50" data-id="element-3678">
        <Container size="lg" data-id="element-3679">
          <div className="text-center mb-8 sm:mb-12" data-id="element-3680">
            <div className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-medium mb-4 border border-cyan-500/20" data-id="element-3681">
              <Award className="h-3 w-3 mr-2" data-id="element-3682" />
              {t('home.standards.badge')}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-4 px-4" data-id="element-3683">
              {t('home.standards.title')}
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto px-4" data-id="element-3684">
              {t('home.standards.desc')}
            </p>
          </div>

          {/* Responsive Standards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12" data-id="element-3685">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 sm:p-6 hover:border-cyan-500/30 transition-all group" data-id="element-3686">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-cyan-500/20 transition-colors" data-id="element-3687">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-500" data-id="element-3688" />
              </div>
              <h3 className="font-bold text-slate-100 mb-2 text-sm sm:text-base" data-id="element-3689">
                {t('home.standards.risk.title')}
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4" data-id="element-3690">
                {t('home.standards.risk.desc')}
              </p>
              <div className="flex items-center text-xs text-cyan-400" data-id="element-3691">
                <CheckCircle className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-3692" />
                <span className="break-words" data-id="element-3693">
                  {t('home.standards.risk.functions')}
                </span>
              </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 sm:p-6 hover:border-cyan-500/30 transition-all group" data-id="element-3694">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-cyan-500/20 transition-colors" data-id="element-3695">
                <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-500" data-id="element-3696" />
              </div>
              <h3 className="font-bold text-slate-100 mb-2 text-sm sm:text-base" data-id="element-3697">
                {t('home.standards.controls.title')}
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4" data-id="element-3698">
                {t('home.standards.controls.desc')}
              </p>
              <div className="flex items-center text-xs text-cyan-400" data-id="element-3699">
                <CheckCircle className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-3700" />
                <span data-id="element-3701">{t('home.standards.controls.count')}</span>
              </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 sm:p-6 hover:border-cyan-500/30 transition-all group sm:col-span-2 md:col-span-1" data-id="element-3702">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-cyan-500/20 transition-colors" data-id="element-3703">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-500" data-id="element-3704" />
              </div>
              <h3 className="font-bold text-slate-100 mb-2 text-sm sm:text-base" data-id="element-3705">
                {t('home.standards.practices.title')}
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4" data-id="element-3706">
                {t('home.standards.practices.desc')}
              </p>
              <div className="flex items-center text-xs text-cyan-400" data-id="element-3707">
                <CheckCircle className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-3708" />
                <span data-id="element-3709">{t('home.standards.practices.count')}</span>
              </div>
            </div>
          </div>

          <div className="text-center" data-id="element-3710">
            <Link to="/about/standards" data-id="element-3711">
              <Button variant="outline" size="lg" className="w-full sm:w-auto" data-id="element-3712">
                {t('home.standards.cta')}
              </Button>
            </Link>
          </div>
        </Container>
      </Section>
    </Layout>;
}